import { AccommodationPlaceRoomBlockStatus, AccommodationPlaceType } from '@graphql/generated';
import { AccommodationCombinedType, AccommodationPlacesV2 } from './Accommodation.types';
import { isNull } from 'lodash-es';
import { distanceInMiles } from '@shared/utils/distanceInMiles';

interface AccommodationTypeArgs {
  type?: AccommodationPlaceType | null;
  zhId?: string | null;
  customUrl?: string | null;
  isCustomLinkEnabled?: boolean | null;
  accommodationsMyOwnLinkEnabled: boolean;
}
const transformToAccommodationType = ({ type, customUrl, isCustomLinkEnabled, zhId, accommodationsMyOwnLinkEnabled }: AccommodationTypeArgs) => {
  const hasCustomLink = accommodationsMyOwnLinkEnabled ? isCustomLinkEnabled : customUrl;
  return hasCustomLink || !zhId ? AccommodationCombinedType.Custom : AccommodationCombinedType.Hotel;
};

export const AccommodationPlacesV2ToAccommodationCombined = (
  accommodationPlacesV2: AccommodationPlacesV2,
  accommodationsMyOwnLinkEnabled: boolean,
  origin: { latitude?: number | null; longitude?: number | null } | null | undefined,
) => {
  const isRoomBlock = !isNull(accommodationPlacesV2?.accommodationRoomBlock) && accommodationPlacesV2?.accommodationRoomBlock?.status === AccommodationPlaceRoomBlockStatus.active;
  const latitude = accommodationPlacesV2?.lat;
  const longitude = accommodationPlacesV2?.long;
  let distance: number | null = null;
  distance = distanceInMiles(origin, { lat: latitude, lng: longitude });
  return {
    id: accommodationPlacesV2.id,
    type: isRoomBlock
      ? AccommodationCombinedType.Roomblock
      : transformToAccommodationType({
          type: accommodationPlacesV2.type,
          zhId: accommodationPlacesV2.zhId,
          customUrl: accommodationPlacesV2.customUrl,
          isCustomLinkEnabled: accommodationPlacesV2.isCustomLinkEnabled,
          accommodationsMyOwnLinkEnabled,
        }),
    accommodationType: accommodationPlacesV2.type,

    //   General Information
    displayName: accommodationPlacesV2?.customName || accommodationPlacesV2?.name,
    address: accommodationPlacesV2?.customAddress || accommodationPlacesV2?.address,
    customAddress: accommodationPlacesV2?.customAddress,
    city: accommodationPlacesV2?.city,
    country: accommodationPlacesV2?.country,
    state: accommodationPlacesV2?.state,
    postalCode: accommodationPlacesV2?.postalCode,
    latitude: accommodationPlacesV2?.lat,
    longitude: accommodationPlacesV2?.long,
    distance: distance,

    description: accommodationPlacesV2.userNote,
    email: accommodationPlacesV2?.email,
    phone: accommodationPlacesV2?.customPhone || accommodationPlacesV2?.phone,
    url: accommodationPlacesV2?.customUrl || accommodationPlacesV2?.url,
    customUrl: accommodationPlacesV2?.customUrl,

    // JoyPlaceID
    joyPlaceId: accommodationPlacesV2?.joyPlaceId,

    //   Provider Information
    googlePlaceId: accommodationPlacesV2?.googlePlaceId,
    zhId: accommodationPlacesV2?.zhId,

    //   Extra Information
    pricePerNight: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.pricePerNight : undefined,
    bookingUrl: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.bookingUrl : undefined,
    cutoffDate: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.cutoffDate : undefined,
    checkOutDate: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.checkOutDate : undefined,
    checkInDate: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.checkInDate : undefined,
    strikeoutPricePerNight: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.strikeoutPricePerNight : undefined,
    numberOfRoomsReserved: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.numberOfRoomsReserved : undefined,
    isJoyManagedRoomBlock: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.isJoyManagedRoomBlock : undefined,

    //   Custom Properties
    isFavorite: accommodationPlacesV2?.isFavorite,
    starRating: accommodationPlacesV2?.starRating,

    photo: accommodationPlacesV2.isPhotoHidden ? null : accommodationPlacesV2.customPhoto || accommodationPlacesV2.photo,
    isFromAccommodationCatalog: accommodationPlacesV2.isFromAccommodationCatalog,
    isCustomLinkEnabled: accommodationPlacesV2.isCustomLinkEnabled,
  };
};

/**
 * To check does accommodation place have link
 */
export const hasAccommodationLink = (
  accommodationsMyOwnLinkEnabled: boolean,
  isCustomLinkEnabled?: boolean | null,
  customUrl?: string | null,
  url?: string | null,
  zhId?: string | null,
): boolean => {
  // Check amplitude flag is off
  if (!accommodationsMyOwnLinkEnabled) return true;

  // Only return true if there's a custom URL and isCustomLinkEnabled is enabled
  if (isCustomLinkEnabled) return Boolean(customUrl);

  // Otherwise, got to customUrl or url or google map url
  return true;
};
